import { useCallback, useState } from 'react'
import { Linking, Platform } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { PRODUCTION_APP_ORIGIN } from '../../../constants/env'
import { useDialog } from '../../shared/hooks/useDialog'
import {
  CONSUMER_PRO_BILLING_INTERVAL,
  type ConsumerProBillingInterval,
} from '../../packs/constants/consumerProBilling.constants'
import {
  PRICING_COPY,
  buildUpgradeMonthlyToYearlyProDialogMessage,
} from '../../packs/constants/pricingPlans.constants'
import { getWebAppOriginForStripeBillingRedirects } from '../helpers/consumerBillingWebOrigin.helper'
import { useLanguage } from '../../i18n/useLanguage'

interface ApiEnvelope<T> {
  success?: boolean
  data?: T
  error?: string
}

/**
 * Same-origin returns (e.g. /packs after cadence switch) use the router so the SPA is not
 * hard-reloaded; Stripe-hosted URLs still use full navigation / Linking.
 */
function tryNavigateSameOriginWithRouter(
  url: string,
  replace: (href: Href) => void,
): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return false
  }
  try {
    const parsed = new URL(url, window.location.href)
    if (parsed.origin !== window.location.origin) {
      return false
    }
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`
    replace(path as Href)
    return true
  } catch {
    return false
  }
}

function redirectToBillingUrl(url: string, replace: (href: Href) => void): void {
  if (tryNavigateSameOriginWithRouter(url, replace)) {
    return
  }
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(url)
    return
  }
  void Linking.openURL(url)
}

/**
 * Individual (consumer) Pro — Stripe checkout + portal; no B2B org.
 */
export function useConsumerProBillingActions(consumerBilling?: {
  stripeBillingEnabled?: boolean
  planTier?: 'FREE' | 'PRO'
  proBillingInterval?: 'month' | 'year' | null
  stripeSubscriptionStatus?: string | null
}) {
  const router = useRouter()
  const apiClient = useApiClient()
  const { showAlert, showDialog } = useDialog()
  const { language } = useLanguage()
  const [busy, setBusy] = useState(false)

  const stripeConfigured = consumerBilling?.stripeBillingEnabled !== false
  const isMonthlyPro =
    consumerBilling?.planTier === 'PRO' && consumerBilling?.proBillingInterval === 'month'

  const startConsumerProCheckout = useCallback(
    async (billingInterval: ConsumerProBillingInterval = CONSUMER_PRO_BILLING_INTERVAL.MONTH) => {
      if (Platform.OS !== 'web') {
        await showAlert({
          title: 'Subscribe on the web',
          message: 'To upgrade to Pro, visit zporti.com/packs in a browser and sign in with your account.',
        })
        return
      }
      if (isMonthlyPro && billingInterval === CONSUMER_PRO_BILLING_INTERVAL.YEAR) {
        const confirmed = await showDialog({
          title: PRICING_COPY.UPGRADE_TO_YEARLY_PRO_TITLE,
          message: buildUpgradeMonthlyToYearlyProDialogMessage(),
          confirmText: PRICING_COPY.UPGRADE_TO_YEARLY_PRO_CONFIRM,
          cancelText: 'Cancel',
        })
        if (!confirmed) return
      }

      setBusy(true)
      try {
        const webAppOrigin = getWebAppOriginForStripeBillingRedirects()
        const res = await apiClient<ApiEnvelope<{ url: string }>>(
          API_ENDPOINTS.billing.consumerProCheckoutSession,
          {
            method: 'POST',
            data: {
              billingInterval,
              locale: language,
              ...(webAppOrigin != null ? { webAppOrigin } : {}),
            },
          },
        )
        const envelope = res.data
        const url = envelope?.data?.url
        if (!envelope?.success || !url) {
          await showAlert({
            title: 'Billing',
            message: extractErrorMessage(envelope, 'Could not start checkout.'),
            type: 'danger',
          })
          return
        }
        redirectToBillingUrl(url, router.replace)
      } catch (e) {
        const httpStatus = (e as { response?: { status?: number } })?.response?.status
        if (httpStatus === 409) {
          const msg = extractErrorMessage(
            e,
            'You already have an active subscription. Use Manage subscription to make changes.',
          )
          const cadenceMsg = msg.toLowerCase().includes('billing cadence')
          await showAlert({
            title: cadenceMsg ? 'Already on this billing interval' : 'Subscription active',
            message: cadenceMsg
              ? `${msg}\n\nIf you just upgraded or changed plans and this surprises you, refresh the page — the screen may be showing an old plan until it reloads.`
              : msg,
            type: 'default',
          })
          return
        }
        await showAlert({
          title: 'Billing',
          message: extractErrorMessage(e, 'Could not start checkout.'),
          type: 'danger',
        })
      } finally {
        setBusy(false)
      }
    },
    [apiClient, language, router.replace, showAlert, showDialog, isMonthlyPro],
  )

  const openConsumerProPortal = useCallback(async (options?: { returnPath?: string }) => {
    if (Platform.OS !== 'web') {
      // This Pro is billed on the web (Stripe), which the OS store can't manage. Offer to open the
      // web app directly rather than asking the user to type the URL; sign-in happens there.
      const confirmed = await showDialog({
        title: 'Manage subscription on the web',
        message: 'Your Pro subscription is billed on the web and is managed at zporti.com/packs. Open it in your browser now?',
        confirmText: 'Open in browser',
        cancelText: 'Not now',
      })
      if (confirmed) {
        void Linking.openURL(`${PRODUCTION_APP_ORIGIN}/packs`)
      }
      return
    }
    setBusy(true)
    try {
      const webAppOrigin = getWebAppOriginForStripeBillingRedirects()
      const returnPath = typeof options?.returnPath === 'string' && options.returnPath.length > 0
        ? options.returnPath
        : undefined
      const res = await apiClient<ApiEnvelope<{ url: string }>>(
        API_ENDPOINTS.billing.consumerProPortalSession,
        {
          method: 'POST',
          data: {
            locale: language,
            ...(webAppOrigin != null ? { webAppOrigin } : {}),
            ...(returnPath != null ? { returnPath } : {}),
          },
        },
      )
      const envelope = res.data
      const url = envelope?.data?.url
      if (!envelope?.success || !url) {
        await showAlert({
          title: 'Billing',
          message: extractErrorMessage(envelope, 'Could not open billing portal.'),
          type: 'danger',
        })
        return
      }
      redirectToBillingUrl(url, router.replace)
    } catch (e) {
      await showAlert({
        title: 'Billing',
        message: extractErrorMessage(e, 'Could not open billing portal.'),
        type: 'danger',
      })
    } finally {
      setBusy(false)
    }
  }, [apiClient, language, router.replace, showAlert, showDialog])

  return { startConsumerProCheckout, openConsumerProPortal, busy, stripeConfigured }
}
